import { Box, CircularProgress, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useMap } from '@vis.gl/react-google-maps';

import { ROUTES } from '../../constants/routes';
import { MobileMap } from '../../components/features/Map/NativeMap';
import { StationsLocationsTypes } from '../../components/features/Map/types';
import { MapSearchBar } from '../../components/features/Map/MapSearchBar';
import { MapFilterDrawer } from '../../components/features/Map/MapFilterDrawer';
import { MapControls } from '../../components/features/Map/MapControls';
import { StationPreviewSheet } from '../../components/features/Map/StationPreviewSheet';
import { MapFilters, countActiveFilters } from '../../components/features/Map/filterTypes';
import { useStationSearch } from '../../components/features/Map/useStationSearch';
import { ConnectorTypeEnum } from '@shared/enums/connector-type';

const DEFAULT_CENTER = { lat: 50.619163, lng: 26.25213 };

const filtersFromSearchParams = (sp: URLSearchParams): MapFilters => ({
  search: sp.get('q') ?? '',
  connectorTypes: (sp.get('ct')?.split(',').filter(Boolean) ??
    []) as ConnectorTypeEnum[],
  availableNow: sp.get('avail') === '1',
  fastCharging: sp.get('fast') === '1',
});

const filtersToSearchParams = (filters: MapFilters): URLSearchParams => {
  const sp = new URLSearchParams();
  if (filters.search) sp.set('q', filters.search);
  if (filters.connectorTypes.length)
    sp.set('ct', filters.connectorTypes.join(','));
  if (filters.availableNow) sp.set('avail', '1');
  if (filters.fastCharging) sp.set('fast', '1');
  return sp;
};

export const MapPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFiltersState] = useState<MapFilters>(() =>
    filtersFromSearchParams(searchParams),
  );
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState<StationsLocationsTypes | null>(null);

  const setFilters = useCallback(
    (next: MapFilters) => {
      setFiltersState(next);
      setSearchParams(filtersToSearchParams(next), { replace: true });
    },
    [setSearchParams],
  );

  const { stations, loading } = useStationSearch(filters);

  const stationPoints = useMemo(
    () =>
      stations.map((s, i) => ({
        geometry: {
          type: 'Point' as const,
          coordinates: [Number(s.longitude), Number(s.latitude)] as [
            number,
            number,
          ],
        },
        connectors: s.connectors,
        stationStatus: s.status,
        _id: s._id ?? `idx-${i}`,
      })),
    [stations],
  );

  const stationByKey = useMemo(() => {
    const map = new Map<string, StationsLocationsTypes>();
    stations.forEach((s, i) => map.set(`point-${i}`, s));
    return map;
  }, [stations]);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [panTarget, setPanTarget] = useState<{
    coords: google.maps.LatLngLiteral;
    zoom?: number;
    nonce: number;
  } | null>(null);

  const handleMarkerClick = (
    coords: google.maps.LatLngLiteral,
    key: string,
  ) => {
    const station = stationByKey.get(key);
    if (station) {
      setSelected(station);
      setSelectedKey(key);
      setPanTarget({ coords, zoom: 15, nonce: Date.now() });
    }
  };

  const handleSelectFromSearch = (station: StationsLocationsTypes) => {
    setSelected(station);
    const idx = stations.findIndex((s) => s._id && s._id === station._id);
    if (idx >= 0) setSelectedKey(`point-${idx}`);
    const lat = Number(station.latitude);
    const lng = Number(station.longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      setPanTarget({ coords: { lat, lng }, zoom: 15, nonce: Date.now() });
    }
  };

  const handleSelectPlace = (lat: number, lng: number) => {
    setPanTarget({ coords: { lat, lng }, zoom: 14, nonce: Date.now() });
  };

  const handleGoToStation = (station: StationsLocationsTypes) => {
    if (station._id) {
      navigate(`/${ROUTES.CABINET}/${ROUTES.CAB_STATION}/${station._id}`);
    }
  };

  const activeFiltersCount = countActiveFilters(filters);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: 'calc(100dvh - 61px - 57px)',
        overflow: 'hidden',
        backgroundColor: 'grey.900',
      }}
    >
      <MobileMap
        defaultCenter={DEFAULT_CENTER}
        stationPoints={stationPoints}
        onMarkerClick={handleMarkerClick}
        selectedKey={selectedKey}
        fillContainer
      >
        <MapSideEffects panTarget={panTarget} />
        <MapControls bottomOffset={selected ? 280 : 24} />
      </MobileMap>

      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          right: 12,
          zIndex: 5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          pointerEvents: 'none',
          '& > *': { pointerEvents: 'auto' },
        }}
      >
        <MapSearchBar
          activeFiltersCount={activeFiltersCount}
          onSelectStation={handleSelectFromSearch}
          onSelectPlace={handleSelectPlace}
          onOpenFilters={() => setDrawerOpen(true)}
        />
      </Box>

      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 96,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 4,
            backgroundColor: 'rgba(20,20,20,0.92)',
            backdropFilter: 'blur(12px)',
            border: '1px solid',
            borderColor: 'grey.800',
            borderRadius: '999px',
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={14} sx={{ color: 'green.main' }} />
          <Stack
            component="span"
            sx={{ color: 'grey.300', fontSize: 12, fontWeight: 500 }}
          >
            Loading…
          </Stack>
        </Box>
      )}

      <StationPreviewSheet
        station={selected}
        onClose={() => {
          setSelected(null);
          setSelectedKey(null);
        }}
        onGoToStation={handleGoToStation}
      />

      <MapFilterDrawer
        open={drawerOpen}
        filters={filters}
        onClose={() => setDrawerOpen(false)}
        onChange={setFilters}
      />
    </Box>
  );
};

const MapSideEffects = ({
  panTarget,
}: {
  panTarget: {
    coords: google.maps.LatLngLiteral;
    zoom?: number;
    nonce: number;
  } | null;
}) => {
  const map = useMap();

  useEffect(() => {
    if (!map || !panTarget) return;
    map.panTo(panTarget.coords);
    if (panTarget.zoom != null) {
      const z = map.getZoom() ?? 13;
      if (z < panTarget.zoom) map.setZoom(panTarget.zoom);
    }
  }, [map, panTarget]);

  return null;
};
